import React from "react";
import { Lock, Shield, ShieldCheck, Zap, Banknote, Smartphone, Mail, ArrowRightLeft, Building2 } from "lucide-react";

/**
 * Premium, monochrome trust cues used near primary CTAs.
 *
 * Two audiences:
 *   - "consumer" (default): reinforces funding speed, payout methods, and confidentiality
 *   - "attorney": reinforces secure institutional access (NO payout method logos)
 *
 * Three variants:
 *   - "payout-strip"  → thin row of payout-method icons with caption (consumer only)
 *   - "trust-line"    → minimal one-line lockup of subtle trust messages
 *   - "pill"          → combined fintech pill with all of the above (most prominent)
 *
 * Designed to sit DIRECTLY beneath a primary CTA. Defaults to muted greys so
 * it never competes visually with the action above it.
 */
export default function TrustCues({
  variant = "trust-line",
  audience = "consumer",
  align = "center",
  className = "",
  testid,
}) {
  const id = testid || `trust-cues-${audience}-${variant}`;
  const alignClass = align === "left" ? "text-left items-start"
                    : align === "right" ? "text-right items-end"
                    : "text-center items-center";

  if (variant === "payout-strip" && audience === "consumer") {
    return (
      <div data-testid={id} className={`mt-4 flex flex-col gap-2.5 ${alignClass} ${className}`}>
        <div className={`flex items-center gap-3 lg:gap-4 text-[#6B7280] ${align === "center" ? "justify-center" : ""}`}>
          <PayoutGlyph icon={Banknote} label="ACH" />
          <PayoutGlyph icon={Smartphone} label="Zelle" />
          <PayoutGlyph icon={ArrowRightLeft} label="Cash App" />
          <PayoutGlyph icon={Mail} label="Overnight check" />
          <PayoutGlyph icon={Building2} label="Wire" />
        </div>
        <p className="text-[11px] text-[#6B7280] tracking-wide">
          Funding available via ACH, Zelle, Cash App, or overnight check — typically in 24–48 hours.
        </p>
      </div>
    );
  }

  if (variant === "pill") {
    if (audience === "consumer") {
      return (
        <div data-testid={id} className={`mt-4 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 px-4 py-2.5 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] ${className}`}>
          <Cue icon={ShieldCheck} label="Encrypted & confidential" />
          <Divider />
          <Cue icon={Zap} label="Funds in 24–48 hours" />
          <Divider />
          <Cue icon={Banknote} label="ACH · Zelle · Cash App · Check" />
        </div>
      );
    }
    return (
      <div data-testid={id} className={`mt-4 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 px-4 py-2.5 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] ${className}`}>
        <Cue icon={Shield} label="Bank-level encryption" />
        <Divider />
        <Cue icon={ShieldCheck} label="Firm-level access controls" />
        <Divider />
        <Cue icon={Lock} label="Secure attorney portal" />
      </div>
    );
  }

  // Default: trust-line
  if (audience === "attorney") {
    return (
      <div data-testid={id} className={`mt-4 flex ${align === "center" ? "justify-center" : ""} ${className}`}>
        <p className="text-[11px] text-[#6B7280] tracking-wide flex flex-wrap items-center gap-x-2 gap-y-1 justify-center">
          <Lock className="w-3 h-3" />
          <span>Bank-level encryption</span>
          <span className="opacity-50">·</span>
          <span>Firm-level access controls</span>
          <span className="opacity-50">·</span>
          <span>Real-time balances and payoff management</span>
        </p>
      </div>
    );
  }
  return (
    <div data-testid={id} className={`mt-4 flex ${align === "center" ? "justify-center" : ""} ${className}`}>
      <p className="text-[11px] text-[#6B7280] tracking-wide flex flex-wrap items-center gap-x-2 gap-y-1 justify-center">
        <ShieldCheck className="w-3 h-3" />
        <span>Encrypted &amp; confidential</span>
        <span className="opacity-50">·</span>
        <span>Attorney verified before funding</span>
        <span className="opacity-50">·</span>
        <span>Funds in 24–48 hours</span>
      </p>
    </div>
  );
}

function PayoutGlyph({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1" title={label}>
      <div className="w-9 h-9 rounded-lg border border-[#E5E7EB] bg-white flex items-center justify-center text-[#9CA3AF]">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
    </div>
  );
}

function Cue({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0A2540] tracking-wide">
      <Icon className="w-3 h-3 text-[#0066FF]" strokeWidth={2} />
      {label}
    </span>
  );
}

function Divider() {
  return <span className="w-px h-3 bg-[#E5E7EB]" aria-hidden />;
}
